import { useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import { createApiCall } from 'services'

import type { IPaginationResult, IProvenanceHistoryItem } from '@repo/types'

type UseProvenanceParams = {
  limit?: number
  offset?: number
}

type Data = IPaginationResult<IProvenanceHistoryItem>

type UseProvenanceResult = {
  data: Data | undefined
  isLoading: boolean
  isRefetching: boolean
  refetch: (params?: UseProvenanceParams) => void
}

const fetchProvenance = async (
  certificateId: string,
  params: UseProvenanceParams
): Promise<Data> => {
  const { data } = await createApiCall({
    method: 'GET',
    url: `/certificates/customer/provenance/${certificateId}`,
    params,
    throwError: true,
  })

  return data as Data
}

// VERA-924/VERA-925: the owner's Provenance tab must use the CUSTOMER
// provenance endpoint, not the public one — the public response has no
// `acquired_via` (gift/sell/primary) and collapses every row's date to the
// certificate's single `date_of_ownership` instead of each transfer's own
// `created_at`. See apps/api certificate-data.aggregate.ts
// toProvenanceHistoryItem() vs toProvenanceHistoryItemPublic().
export const useProvenance = (
  certificateId: string | undefined,
  params: UseProvenanceParams = {}
): UseProvenanceResult => {
  const [queryParams, setQueryParams] = useState<UseProvenanceParams>(params)

  const {
    data,
    isLoading,
    isFetching,
    refetch: queryRefetch,
  } = useQuery({
    queryKey: ['provenance', certificateId, queryParams],
    queryFn: () => fetchProvenance(certificateId as string, queryParams),
    enabled: !!certificateId,
  })

  const refetch = (nextParams?: UseProvenanceParams) => {
    if (nextParams) {
      setQueryParams(prev => ({ ...prev, ...nextParams }))
      return
    }

    queryRefetch()
  }

  return {
    data,
    isLoading,
    isRefetching: isFetching && !isLoading,
    refetch,
  }
}
